import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

export default function GuiaProducaoSemanal() {
  const [tamanhosCongelados, setTamanhosCongelados] = useState([]);
  const [tamanhosImediatos, setTamanhosImediatos] = useState([]);
  const [menuFixo, setMenuFixo] = useState([]);
  const [fichasMassa, setFichasMassa] = useState([]);
  const [recheiosDisponiveis, setRecheiosDisponiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [calculando, setCalculando] = useState(false);

  const [quantidades, setQuantidades] = useState({});
  const [resultado, setResultado] = useState(null);
  const [fichasDetalhes, setFichasDetalhes] = useState({});
  const [fichasRecheio, setFichasRecheio] = useState({});
  const [modalReceita, setModalReceita] = useState(null); // { nome, nReceitas, ficha }
  const resultadoRef = useRef(null);

  // Config modal
  const [configAberto, setConfigAberto] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);

  useEffect(() => { carregarDados(); }, []);

  async function carregarDados() {
    setLoading(true);
    try {
      const [resCongelados, resImediatos, resMenu, resFichas, resRecheios] = await Promise.all([
        api.get('/guia/tamanhos?tipo=congelado'),
        api.get('/guia/tamanhos?tipo=imediato'),
        api.get('/guia/menu-fixo'),
        api.get('/fichas/api/fichas-tecnicas'),
        api.get('/guia/recheios'),
      ]);
      setTamanhosCongelados(resCongelados.data);
      setTamanhosImediatos(resImediatos.data);
      setMenuFixo(resMenu.data);
      const listaFichas = resFichas.data?.fichas || [];
      setFichasMassa(listaFichas.filter(f => f.categoria === 'Massas de Bolo'));
      setRecheiosDisponiveis(resRecheios.data || []);
    } catch {
      setErro('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }

  function chaveCongelado(tamanho_id, massa_ficha_id, recheio_id) {
    return `${tamanho_id}_${massa_ficha_id}_${recheio_id}`;
  }

  function chaveImediato(tamanho_id, massa_ficha_id) {
    return `${tamanho_id}_${massa_ficha_id}_null`;
  }

  function setQtd(chave, valor) {
    const n = Math.max(0, parseInt(valor) || 0);
    setQuantidades(prev => ({ ...prev, [chave]: n }));
  }

  function getQtd(chave) {
    return quantidades[chave] || 0;
  }

  const totalBolos = Object.values(quantidades).reduce((a, b) => a + b, 0);

  // ── Monta breakdown por tamanho para cada massa (imediatos) ──────────────────
  function breakdownPorMassa(fichaId) {
    return tamanhosImediatos
      .map(t => {
        const chave = chaveImediato(t.id, fichaId);
        const qtd = getQtd(chave);
        return { tamanho: t, qtd, massaCrua: qtd * (t.massa_crua_por_forma || 0) };
      })
      .filter(r => r.qtd > 0);
  }

  // ── Calcular ─────────────────────────────────────────────────────────────────
  async function calcular(e) {
    e.preventDefault();
    setSalvando(true);
    setErro('');
    setResultado(null);

    const itens = [];
    menuFixo.forEach(m => {
      const chave = chaveCongelado(m.tamanho_id, m.massa_ficha_id, m.recheio_id);
      const qtd = getQtd(chave);
      if (qtd > 0) itens.push({ tamanho_id: m.tamanho_id, massa_ficha_id: m.massa_ficha_id, recheio_id: m.recheio_id, quantidade: qtd });
    });
    tamanhosImediatos.forEach(t => {
      fichasMassa.forEach(f => {
        const chave = chaveImediato(t.id, f.id);
        const qtd = getQtd(chave);
        if (qtd > 0) itens.push({ tamanho_id: t.id, massa_ficha_id: f.id, quantidade: qtd });
      });
    });

    if (itens.length === 0) {
      setErro('Informe pelo menos uma quantidade maior que zero.');
      setSalvando(false);
      return;
    }

    try {
      const res = await api.post('/guia/producoes', {
        data_referencia: new Date().toISOString().split('T')[0],
        buffer_percentual: 0,
        itens,
      });
      setCalculando(true);
      const resCalc = await api.get(`/guia/producoes/${res.data.id}/calcular`);
      const dadosResultado = resCalc.data;
      // Buscar fichas de massa
      const detalhes = {};
      await Promise.all(
        fichasMassa.map(async f => {
          try {
            const res = await api.get(`/fichas/api/fichas-tecnicas/${f.id}`);
            if (res.data.success) detalhes[f.nome] = res.data.ficha;
          } catch {}
        })
      );
      // Buscar fichas de recheio
      const detRecheio = {};
      await Promise.all(
        (dadosResultado.recheios_congelados || []).map(async r => {
          const info = recheiosDisponiveis.find(x => x.id === r.recheio_id);
          if (!info) return;
          if (info.base_ficha_id) {
            try {
              const res = await api.get(`/fichas/api/fichas-tecnicas/${info.base_ficha_id}`);
              if (res.data.success) detRecheio[r.base.nome] = res.data.ficha;
            } catch {}
          }
          if (info.complemento_ficha_id) {
            try {
              const res = await api.get(`/fichas/api/fichas-tecnicas/${info.complemento_ficha_id}`);
              if (res.data.success && r.complemento) detRecheio[r.complemento.nome] = res.data.ficha;
            } catch {}
          }
        })
      );
      setFichasDetalhes(detalhes);
      setFichasRecheio(detRecheio);
      setResultado(dadosResultado);
      setTimeout(() => resultadoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao calcular produção');
    } finally {
      setSalvando(false);
      setCalculando(false);
    }
  }


  // ── Histórico ─────────────────────────────────────────────────────────────────
  async function carregarHistorico() {
    setLoadingHistorico(true);
    try { const res = await api.get('/guia/producoes'); setHistorico(res.data || []); }
    catch { setHistorico([]); }
    finally { setLoadingHistorico(false); }
  }

  async function excluirProducao(id) {
    if (!window.confirm('Excluir esta produção?')) return;
    try {
      await api.delete(`/guia/producoes/${id}`);
      setHistorico(prev => prev.filter(h => h.id !== id));
    } catch { alert('Erro ao excluir produção.'); }
  }

  async function reabrirProducao(id) {
    try {
      const [resP, resCalc] = await Promise.all([
        api.get(`/guia/producoes/${id}`),
        api.get(`/guia/producoes/${id}/calcular`),
      ]);
      // Restaurar quantidades
      const novasQtds = {};
      (resP.data.itens || []).forEach(i => {
        const chave = i.recheio_id
          ? chaveCongelado(i.tamanho_id, i.massa_ficha_id, i.recheio_id)
          : chaveImediato(i.tamanho_id, i.massa_ficha_id);
        novasQtds[chave] = i.quantidade;
      });
      setQuantidades(novasQtds);
      // Buscar fichas
      const detalhes = {};
      await Promise.all(fichasMassa.map(async f => {
        try {
          const r = await api.get(`/fichas/api/fichas-tecnicas/${f.id}`);
          if (r.data.success) detalhes[f.nome] = r.data.ficha;
        } catch {}
      }));
      const detRecheio = {};
      await Promise.all((resCalc.data.recheios_congelados || []).map(async r => {
        const info = recheiosDisponiveis.find(x => x.id === r.recheio_id);
        if (!info) return;
        if (info.base_ficha_id) {
          try { const res = await api.get(`/fichas/api/fichas-tecnicas/${info.base_ficha_id}`); if (res.data.success) detRecheio[r.base.nome] = res.data.ficha; } catch {}
        }
        if (info.complemento_ficha_id && r.complemento) {
          try { const res = await api.get(`/fichas/api/fichas-tecnicas/${info.complemento_ficha_id}`); if (res.data.success) detRecheio[r.complemento.nome] = res.data.ficha; } catch {}
        }
      }));
      setFichasDetalhes(detalhes);
      setFichasRecheio(detRecheio);
      setResultado(resCalc.data);
      setHistoricoAberto(false);
      setTimeout(() => resultadoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch { alert('Erro ao reabrir produção.'); }
  }

  if (loading) return <div className="text-center py-12 text-gray-400">Carregando...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Produção Semanal</h1>
          <p className="text-gray-500 text-sm mt-1">Informe as quantidades e calcule o que preparar</p>
        </div>
        <button onClick={() => { setHistoricoAberto(true); carregarHistorico(); }}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Histórico">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      </div>

      {erro && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm">{erro}</div>}

      <form onSubmit={calcular} className="space-y-4">

        {/* Congelados */}
        {tamanhosCongelados.length > 0 && (
          <div className="rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-sky-50 px-5 py-3.5 border-b border-blue-100 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center text-base">❄️</div>
              <div>
                <span className="font-semibold text-blue-900 text-base">Bolos Congelados</span>
                <p className="text-xs text-blue-400 mt-0.5">Montagem completa durante a produção</p>
              </div>
            </div>
            <div className="bg-white divide-y divide-gray-50">
              {tamanhosCongelados.map(t => {
                const itensDoTamanho = menuFixo.filter(m => m.tamanho_id === t.id);
                if (itensDoTamanho.length === 0) return null;
                return (
                  <div key={t.id}>
                    <div className="px-5 py-2 bg-gray-50/70 flex items-center gap-2">
                      <span className="font-semibold text-gray-700 text-xs uppercase tracking-wider">{t.nome}</span>
                      <span className="text-xs text-gray-400">· {t.fatias} fatias</span>
                    </div>
                    <div className="px-5 py-3 space-y-2.5">
                      {itensDoTamanho.map(m => {
                        const chave = chaveCongelado(m.tamanho_id, m.massa_ficha_id, m.recheio_id);
                        return (
                          <div key={m.id} className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-gray-700 font-medium truncate block">{m.recheio_nome}</span>
                              <span className="text-xs text-gray-400">{m.massa_ficha_nome}</span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <button type="button" onClick={() => setQtd(chave, getQtd(chave) - 1)}
                                className="w-7 h-7 rounded-lg border border-gray-200 text-gray-400 hover:border-blue-300 hover:text-blue-500 flex items-center justify-center font-bold transition-colors">−</button>
                              <input type="number" min="0" value={getQtd(chave) || ''} onChange={e => setQtd(chave, e.target.value)}
                                placeholder="0"
                                className="w-14 text-center border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-200 bg-gray-50" />
                              <button type="button" onClick={() => setQtd(chave, getQtd(chave) + 1)}
                                className="w-7 h-7 rounded-lg border border-gray-200 text-gray-400 hover:border-blue-300 hover:text-blue-500 flex items-center justify-center font-bold transition-colors">+</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Imediatos */}
        {tamanhosImediatos.length > 0 && fichasMassa.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-50 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-pink-50 flex items-center justify-center text-base">🕐</div>
              <div>
                <span className="font-semibold text-gray-800 text-base">Bolos Imediatos</span>
                <p className="text-xs text-gray-400 mt-0.5">Só a massa é produzida agora</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/70 border-b border-gray-100">
                    <th className="text-left px-5 py-3 font-semibold text-xs uppercase tracking-wider text-gray-400">Tamanho</th>
                    {fichasMassa.map(f => (
                      <th key={f.id} className="text-center px-4 py-3 font-semibold text-xs uppercase tracking-wider text-gray-500">{f.nome}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {tamanhosImediatos.map(t => (
                    <tr key={t.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3 font-medium text-gray-700">
                        {t.nome} <span className="text-xs text-gray-400 font-normal">{t.fatias}f</span>
                      </td>
                      {fichasMassa.map(f => {
                        const chave = chaveImediato(t.id, f.id);
                        return (
                          <td key={f.id} className="px-4 py-2.5 text-center">
                            <input type="number" min="0" value={getQtd(chave) || ''} onChange={e => setQtd(chave, e.target.value)}
                              placeholder="0"
                              className="w-16 text-center border border-gray-200 rounded-xl px-2 py-1.5 text-sm font-semibold bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-200 focus:bg-white transition-colors" />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <span className="text-sm text-gray-400 font-medium">
            {totalBolos > 0 && (
              <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-semibold">
                {totalBolos} bolo{totalBolos > 1 ? 's' : ''} no total
              </span>
            )}
          </span>
          <button type="submit" disabled={salvando || calculando}
            className="bg-pink-500 hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-xl font-semibold text-sm shadow-sm hover:shadow-md transition-all">
            {salvando ? '⏳ Salvando...' : calculando ? '⏳ Calculando...' : resultado ? '✦ Recalcular Produção' : '✦ Calcular Produção'}
          </button>
        </div>
      </form>

      {/* ── Resultado inline ── */}
      {resultado && (
        <>
          <hr className="border-gray-100 my-8" />
          <div ref={resultadoRef} className="space-y-5">

            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">🎂 Guia de Produção</h2>
              <button onClick={() => window.print()}
                className="text-sm text-gray-500 hover:text-gray-700 border border-gray-200 px-3 py-1.5 rounded-lg">
                🖨️ Imprimir
              </button>
            </div>

            {/* Massas */}
            {(resultado.massas || []).map((m, i) => {
              const ficha = fichasDetalhes[m.nome];
              // Calcular totais localmente (mais preciso que m.receitas do backend)
              const todosTamanhos = [...tamanhosCongelados, ...tamanhosImediatos];
              const linhasCalc = todosTamanhos.map(t => {
                const fichaId = fichasMassa.find(f => f.nome === m.nome)?.id;
                let qtd;
                if (t.tipo === 'congelado') {
                  qtd = fichaId ? Object.entries(quantidades)
                    .filter(([k]) => k.startsWith(`${t.id}_${fichaId}_`) && !k.endsWith('_null'))
                    .reduce((s, [, v]) => s + v, 0) : 0;
                } else {
                  qtd = fichaId ? getQtd(chaveImediato(t.id, fichaId)) : 0;
                }
                if (!qtd) return null;
                const discosPorForma = t.discos_por_forma || 2;
                const totalDiscos = (t.discos_camadas || 0) * qtd;
                const formas = Math.ceil(totalDiscos / discosPorForma);
                const massaTotal = formas * (t.massa_crua_por_forma || 0);
                return { massaTotal };
              }).filter(Boolean);
              const totMassaCalc = linhasCalc.reduce((s, r) => s + r.massaTotal, 0);
              const nReceitas = ficha?.peso_total
                ? Math.ceil(totMassaCalc / ficha.peso_total)
                : (m.receitas || 1);
              return (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="font-bold text-gray-800">🧁 Massa: {m.nome}</h3>
                    <span className="bg-pink-100 text-pink-700 text-xs font-bold px-2.5 py-0.5 rounded-full">
                      {nReceitas} receita{nReceitas !== 1 ? 's' : ''}
                    </span>
                    {ficha?.peso_total && (
                      <span className="text-xs text-gray-400">
                        sobra: <span className="font-semibold text-gray-500">
                          {((nReceitas * ficha.peso_total) - totMassaCalc).toLocaleString('pt-BR')}g
                        </span>
                      </span>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-pink-50 text-xs text-gray-500 uppercase tracking-wider">
                          <th className="text-left px-3 py-2 rounded-l-lg">Tamanho</th>
                          <th className="text-center px-3 py-2">Qtd</th>
                          <th className="text-center px-3 py-2">Discos</th>
                          <th className="text-center px-3 py-2 text-gray-400">Discos/forma</th>
                          <th className="text-right px-3 py-2 text-gray-400">g/forma</th>
                          <th className="text-right px-3 py-2">Massa total</th>
                          <th className="text-center px-3 py-2">Receitas</th>
                          <th className="text-center px-3 py-2 rounded-r-lg">Formas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const fichaId = fichasMassa.find(f => f.nome === m.nome)?.id;
                          const linhas = todosTamanhos.map(t => {
                            let qtd;
                            if (t.tipo === 'congelado') {
                              qtd = fichaId ? Object.entries(quantidades)
                                .filter(([k]) => k.startsWith(`${t.id}_${fichaId}_`) && !k.endsWith('_null'))
                                .reduce((s, [, v]) => s + v, 0) : 0;
                            } else {
                              qtd = fichaId ? getQtd(chaveImediato(t.id, fichaId)) : 0;
                            }
                            if (!qtd) return null;
                            const pesoReceita = ficha?.peso_total || null;
                            const discosPorForma = t.discos_por_forma || 2;
                            const totalDiscos = (t.discos_camadas || 0) * qtd;
                            const formas = Math.ceil(totalDiscos / discosPorForma);
                            const massaTotal = formas * (t.massa_crua_por_forma || 0);
                            const receitas = pesoReceita ? Math.ceil(massaTotal / pesoReceita) : null;
                            return { t, qtd, totalDiscos, discosPorForma, massaTotal, formas, receitas };
                          }).filter(Boolean);
                          const totMassa = linhas.reduce((s, r) => s + r.massaTotal, 0);
                          const totFormas = linhas.reduce((s, r) => s + r.formas, 0);
                          const totReceitas = ficha?.peso_total ? Math.ceil(totMassa / ficha.peso_total) : null;
                          return (
                            <>
                              {linhas.map((r, j) => (
                                <tr key={j} className="border-b border-gray-50">
                                  <td className="px-3 py-2 font-medium text-gray-700">{r.t.nome}{r.t.tipo === 'congelado' ? ' ❄️' : ''}</td>
                                  <td className="px-3 py-2 text-center">{r.qtd}</td>
                                  <td className="px-3 py-2 text-center">{r.totalDiscos}</td>
                                  <td className="px-3 py-2 text-center text-gray-400">{r.discosPorForma}</td>
                                  <td className="px-3 py-2 text-right text-gray-400">{r.t.massa_crua_por_forma?.toLocaleString('pt-BR')}g</td>
                                  <td className="px-3 py-2 text-right">{r.massaTotal.toLocaleString('pt-BR')}g</td>
                                  <td className="px-3 py-2 text-center">{r.receitas != null ? `${r.receitas}x` : '—'}</td>
                                  <td className="px-3 py-2 text-center font-bold">{r.formas}</td>
                                </tr>
                              ))}
                              <tr className="bg-gray-50 font-bold text-sm">
                                <td colSpan={5} className="px-3 py-2 text-right text-gray-400 text-xs">TOTAL</td>
                                <td className="px-3 py-2 text-right">{totMassa.toLocaleString('pt-BR')}g</td>
                                <td className="px-3 py-2 text-center">{totReceitas != null ? `${totReceitas}x` : '—'}</td>
                                <td className="px-3 py-2 text-center">{totFormas}</td>
                              </tr>
                              {ficha?.peso_total && totReceitas != null && (
                                <tr className="text-xs text-gray-400">
                                  <td colSpan={8} className="px-3 py-1.5 text-right">
                                    {totReceitas}x × {Number(ficha.peso_total).toLocaleString('pt-BR')}g =&nbsp;
                                    <span className="font-semibold">{(totReceitas * ficha.peso_total).toLocaleString('pt-BR')}g</span>
                                    &nbsp;·&nbsp; sobra:&nbsp;
                                    <span className="font-semibold text-amber-600">
                                      {((totReceitas * ficha.peso_total) - totMassa).toLocaleString('pt-BR')}g
                                    </span>
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>

                  {/* Ingredientes da massa */}
                  {ficha && (() => {
                    const insumos = (ficha.itens || []).filter(i => i.tipo === 'produto' || i.tipo === 'insumo');
                    if (!insumos.length) return null;
                    const cols = Array.from({ length: Math.max(nReceitas, 1) }, (_, i) => i + 1);
                    return (
                      <>
                        <hr className="border-gray-50 my-4" />
                        <details>
                          <summary className="cursor-pointer text-xs font-bold text-gray-400 uppercase tracking-wider select-none">
                            🧂 Ingredientes ({nReceitas}x = quantidade a preparar)
                          </summary>
                          <div className="mt-3 overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-pink-50 text-xs text-gray-500 uppercase tracking-wider">
                                  <th className="text-left px-3 py-2 rounded-l-lg">Ingrediente</th>
                                  <th className="text-center px-3 py-2">Unid.</th>
                                  {cols.map(c => <th key={c} className="text-center px-3 py-2">{c}x</th>)}
                                </tr>
                              </thead>
                              <tbody>
                                {insumos.map((item, j) => (
                                  <tr key={j} className="border-b border-gray-50">
                                    <td className="px-3 py-2 font-medium text-gray-700">{item.nome}</td>
                                    <td className="px-3 py-2 text-center text-gray-400">{item.unidade_medida}</td>
                                    {cols.map(c => {
                                      const v = item.quantidade * c;
                                      return <td key={c} className="px-3 py-2 text-center">{v % 1 === 0 ? v : v.toFixed(2).replace('.', ',')}</td>;
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                        {ficha.modo_preparo && (
                          <details className="mt-3">
                            <summary className="cursor-pointer text-sm text-pink-700 font-semibold select-none">👨‍🍳 Modo de preparo</summary>
                            <div className="mt-2 text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-4 border-l-4 border-pink-200 leading-relaxed">
                              {ficha.modo_preparo}
                            </div>
                          </details>
                        )}
                      </>
                    );
                  })()}
                </div>
              );
            })}

            {/* Recheios dos Congelados */}
            {(resultado.recheios_congelados?.length > 0) && (() => {
              const componentesMap = {};
              resultado.recheios_congelados.forEach(r => {
                const kb = r.base.nome;
                if (!componentesMap[kb]) componentesMap[kb] = { nome: kb, total: 0, receitas: r.base.receitas, ficha_id: r.base.ficha_id, origens: [] };
                componentesMap[kb].total += r.base.total_gramas;
                componentesMap[kb].origens.push({ recheio: r.nome, recheio_id: r.recheio_id, gramas: r.base.total_gramas });
                if (r.complemento) {
                  const kc = r.complemento.nome;
                  if (!componentesMap[kc]) componentesMap[kc] = { nome: kc, total: 0, receitas: r.complemento.receitas, ficha_id: r.complemento.ficha_id, origens: [] };
                  componentesMap[kc].total += r.complemento.total_gramas;
                  componentesMap[kc].origens.push({ recheio: r.nome, recheio_id: r.recheio_id, gramas: r.complemento.total_gramas });
                }
              });
              const componentes = Object.values(componentesMap);
              return (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <h3 className="font-bold text-gray-800 mb-4">❄️ Recheios dos Congelados</h3>
                  <div className="space-y-3">
                    {componentes.map((c, i) => {
                      const fichaComp = fichasRecheio[c.nome];
                      const pesoReceita = fichaComp?.peso_total || null;
                      const receitasNecessarias = pesoReceita ? Math.ceil(c.total / pesoReceita) : null;
                      const nReceitas = receitasNecessarias ?? c.receitas;
                      const sobra = pesoReceita && receitasNecessarias != null
                        ? (receitasNecessarias * pesoReceita) - c.total
                        : null;
                      return (
                        <div key={i} className="pb-3 border-b border-gray-50 last:border-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-800">{c.nome}:</span>
                            <span className="text-gray-700">{c.total.toLocaleString('pt-BR')}g</span>
                            {nReceitas != null
                              ? <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">{nReceitas} receita{nReceitas !== 1 ? 's' : ''}</span>
                              : <span className="text-gray-400 text-xs">pesar direto</span>
                            }
                            {sobra != null && (
                              <span className="text-xs text-gray-400">
                                sobra: <span className="font-semibold text-amber-600">{sobra.toLocaleString('pt-BR')}g</span>
                              </span>
                            )}
                            {fichaComp && nReceitas && (
                              <button onClick={() => setModalReceita({ nome: c.nome, nReceitas, ficha: fichaComp })}
                                className="text-xs text-pink-600 border border-pink-200 hover:bg-pink-50 px-2 py-0.5 rounded-lg">
                                📋 ver receita
                              </button>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                            {c.origens.map((o, j) => {
                              const chaveMatch = Object.keys(quantidades).find(k => {
                                const parts = k.split('_');
                                return parts[2] === String(o.recheio_id) && quantidades[k] > 0;
                              });
                              const tamanhoId = chaveMatch ? Number(chaveMatch.split('_')[0]) : null;
                              const t = tamanhoId ? tamanhosCongelados.find(t => t.id === tamanhoId) : null;
                              return (
                                <div key={j}>{o.gramas.toLocaleString('pt-BR')}g → {o.recheio}{t ? ` (${t.nome})` : ''}</div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Cinta + Calda dos Congelados */}
            {(() => {
              const linhas = tamanhosCongelados.map(t => {
                const qtd = Object.entries(quantidades)
                  .filter(([k]) => k.startsWith(`${t.id}_`) && !k.endsWith('_null'))
                  .reduce((s, [, v]) => s + v, 0);
                if (!qtd) return null;
                const cintaPorBolo = (t.cinta_camadas || 0) * (t.cinta_gramas || 0);
                const caldaPorBolo = (t.calda_camadas || 0) * (t.calda_gramas || 0);
                return {
                  tamanho: t.nome, qtd,
                  cintaPorBolo, cintaTotal: qtd * cintaPorBolo,
                  caldaPorBolo, caldaTotal: qtd * caldaPorBolo,
                };
              }).filter(Boolean);
              if (!linhas.length) return null;
              const totalCinta = linhas.reduce((s, r) => s + r.cintaTotal, 0);
              const totalCalda = linhas.reduce((s, r) => s + r.caldaTotal, 0);
              return (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <h3 className="font-bold text-gray-800 mb-4">❄️ Congelados: Cinta (buttercream) e Calda</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-pink-50 text-xs text-gray-500 uppercase tracking-wider">
                          <th className="text-left px-3 py-2 rounded-l-lg">Tamanho</th>
                          <th className="text-center px-3 py-2">Qtd</th>
                          <th className="text-right px-3 py-2 text-gray-400">Cinta/bolo</th>
                          <th className="text-right px-3 py-2">Total cinta</th>
                          <th className="text-right px-3 py-2 text-gray-400">Calda/bolo</th>
                          <th className="text-right px-3 py-2 rounded-r-lg">Total calda</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhas.map((r, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-700">{r.tamanho}</td>
                            <td className="px-3 py-2 text-center">{r.qtd}</td>
                            <td className="px-3 py-2 text-right text-gray-400">{r.cintaPorBolo ? `${r.cintaPorBolo.toLocaleString('pt-BR')}g` : '—'}</td>
                            <td className="px-3 py-2 text-right font-bold">{r.cintaTotal ? `${r.cintaTotal.toLocaleString('pt-BR')}g` : '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-400">{r.caldaPorBolo ? `${r.caldaPorBolo.toLocaleString('pt-BR')}g` : '—'}</td>
                            <td className="px-3 py-2 text-right font-bold">{r.caldaTotal ? `${r.caldaTotal.toLocaleString('pt-BR')}g` : '—'}</td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-bold text-sm">
                          <td colSpan={3} className="px-3 py-2 text-right text-gray-400 text-xs">TOTAL CINTA</td>
                          <td className="px-3 py-2 text-right">{totalCinta.toLocaleString('pt-BR')}g</td>
                          <td className="px-3 py-2 text-right text-gray-400 text-xs">TOTAL CALDA</td>
                          <td className="px-3 py-2 text-right">{totalCalda.toLocaleString('pt-BR')}g</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

          </div>
        </>
      )}

      {/* ── Modal receita ── */}
      {modalReceita && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={e => e.target === e.currentTarget && setModalReceita(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-800">{modalReceita.ficha.nome}</h2>
                <p className="text-sm text-gray-400 mt-0.5">
                  {modalReceita.nReceitas} receita{modalReceita.nReceitas !== 1 ? 's' : ''}
                  {modalReceita.ficha.peso_total ? ` · ${(modalReceita.ficha.peso_total * modalReceita.nReceitas).toLocaleString('pt-BR')}g total` : ''}
                </p>
              </div>
              <button onClick={() => setModalReceita(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>
            <div className="overflow-y-auto p-6">
              {(() => {
                const insumos = (modalReceita.ficha.itens || []).filter(i => i.tipo === 'produto' || i.tipo === 'insumo');
                const cols = Array.from({ length: Math.max(modalReceita.nReceitas, 1) }, (_, i) => i + 1);
                if (!insumos.length) return <p className="text-gray-400 text-sm">Sem ingredientes cadastrados.</p>;
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-pink-50 text-xs text-gray-500 uppercase tracking-wider">
                          <th className="text-left px-3 py-2 rounded-l-lg">Ingrediente</th>
                          <th className="text-center px-3 py-2">Unidade</th>
                          {cols.map(c => <th key={c} className="text-center px-3 py-2">{c}x</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {insumos.map((item, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-700">{item.nome}</td>
                            <td className="px-3 py-2 text-center text-gray-400">{item.unidade_medida}</td>
                            {cols.map(c => {
                              const v = item.quantidade * c;
                              return <td key={c} className="px-3 py-2 text-center">{v % 1 === 0 ? v : v.toFixed(2).replace('.', ',')}</td>;
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {modalReceita.ficha.modo_preparo && (
                      <details className="mt-4">
                        <summary className="cursor-pointer text-sm text-pink-700 font-semibold">👨‍🍳 Modo de preparo</summary>
                        <div className="mt-2 text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-4 border-l-4 border-pink-200 leading-relaxed">
                          {modalReceita.ficha.modo_preparo}
                        </div>
                      </details>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Histórico ── */}
      {historicoAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800">Histórico de Produções</h2>
              <button onClick={() => setHistoricoAberto(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {loadingHistorico ? (
                <p className="text-center text-gray-400 py-8">Carregando...</p>
              ) : historico.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">Nenhuma produção no histórico.</p>
              ) : (
                <div className="space-y-2">
                  {historico.map(h => {
                    const totalBolosH = (h.itens || []).reduce((s, i) => s + (i.quantidade || 0), 0);
                    const geradoEm = h.criado_em ? new Date(h.criado_em) : null;
                    return (
                      <div key={h.id} className="flex items-center gap-2">
                        <button type="button" onClick={() => reabrirProducao(h.id)}
                          className="flex-1 text-left bg-gray-50 hover:bg-violet-50 border border-gray-100 hover:border-violet-200 rounded-xl px-4 py-3 transition-colors">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-800 text-sm">
                              Semana de {new Date(h.data_referencia + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                            <span className="text-xs text-gray-400">{totalBolosH} bolos</span>
                          </div>
                          {geradoEm && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              Gerado em {geradoEm.toLocaleDateString('pt-BR')} às {geradoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </button>
                        <button type="button" onClick={() => excluirProducao(h.id)}
                          title="Excluir produção"
                          className="shrink-0 text-gray-300 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200 rounded-lg p-2 transition-colors">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}